import * as Sentry from '@sentry/react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes } from 'react-router-dom'
import { AuthGuard } from './components/AuthGuard'
import { SessionProvider } from './contexts/chat/SessionContext'
import { StorageProvider } from './contexts/StorageContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { WalletProvider } from './contexts/WalletContext'
import { WeatherProvider } from './contexts/WeatherContext'
import ArtifactPage from './pages/ArtifactPage'
import AuthPage from './pages/AuthPage'
import HomePage from './pages/HomePage'
import SharedArtifactPage from './pages/SharedArtifactPage'
import SharedIdentityPage from './pages/SharedIdentityPage'
import SharedProfilePage from './pages/SharedProfilePage'
import SharedThemePage from './pages/SharedThemePage'

// Authenticated app wrapper with session providers
function AuthenticatedApp({ children }: { children: React.ReactNode }) {
  return (
    <SessionProvider>
      <StorageProvider>
        <WalletProvider>{children}</WalletProvider>
      </StorageProvider>
    </SessionProvider>
  )
}

export default function App() {
  const { i18n } = useTranslation()

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            padding: '2rem',
            textAlign: 'center',
          }}
        >
          <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>
            Something went wrong
          </h1>
          <p style={{ color: '#666', marginBottom: '2rem' }}>
            {error instanceof Error
              ? error.message
              : 'An unexpected error occurred'}
          </p>
          <button
            onClick={resetError}
            style={{
              padding: '0.75rem 1.5rem',
              fontSize: '1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '0.25rem',
              cursor: 'pointer',
            }}
          >
            Try again
          </button>
        </div>
      )}
      showDialog
    >
      <ThemeProvider key={i18n.language}>
        <WeatherProvider>
          <Routes>
            {/* Auth routes - no session providers */}
            <Route path="/auth/*" element={<AuthPage key={i18n.language} />} />

            {/* Shared content - public, no auth required */}
            <Route path="/profile/:id" element={<SharedProfilePage />} />
            <Route
              path="/artifacts/shared/:id"
              element={<SharedArtifactPage />}
            />
            <Route path="/themes/shared/:id" element={<SharedThemePage />} />
            <Route
              path="/identities/shared/:id"
              element={<SharedIdentityPage />}
            />

            <Route
              path="/"
              element={
                <AuthenticatedApp>
                  <HomePage key={i18n.language} />
                </AuthenticatedApp>
              }
            />

            {/* Artifact viewer */}
            <Route
              path="/artifacts/:id"
              element={
                <AuthGuard>
                  <AuthenticatedApp>
                    <ArtifactPage />
                  </AuthenticatedApp>
                </AuthGuard>
              }
            />

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </WeatherProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  )
}
